import { Mail, Phone, Twitter, Instagram, Linkedin, MapPin } from 'lucide-react';
import type { PersonDetail } from '../../types';
import { effectiveEmail, effectivePhone } from '../../lib/personPrecedence';
import { formatPhone } from '../../lib/formatPhone';
import { useAuthStore } from '../../store/authStore';

interface PersonContactInfoProps {
  person: PersonDetail;
  /** Opens the in-app email composer for this contact (the email address is the trigger — no mailto: handoff). */
  onComposeEmail?: () => void;
  /** Launches "Open in Gmail" (Gmail app on iOS, Gmail web elsewhere) for this contact. */
  onOpenInGmail?: () => void;
}

/**
 * Contact information card
 * Shows email (click to compose in-app), phone (click-to-call), social links
 */
export const PersonContactInfo = ({ person, onComposeEmail, onOpenInGmail }: PersonContactInfoProps) => {
  // Get primary organization for location
  const primaryOrg = person.organizations?.find((o) => o.isPrimary) || person.organizations?.[0];
  const location = primaryOrg
    ? [primaryOrg.organization.city, primaryOrg.organization.state].filter(Boolean).join(', ')
    : null;
  const email = effectiveEmail(person);
  const phone = effectivePhone(person);
  const emailComposeMode = useAuthStore((s) => s.user?.emailComposeMode) ?? 'COMPOSER';

  // Shared between the MAILTO <a> and the COMPOSER <button> so the two
  // branches differ only in the wrapper element, never in duplicated markup.
  const emailClass =
    'flex items-center w-full text-left text-gray-700 dark:text-gray-300 hover:text-blast-600 dark:hover:text-blast-400 transition-colors min-w-0';
  const emailInner = (
    <>
      <Mail className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
      <span className="truncate min-w-0">{email}</span>
      {person.crmEmail !== null && (
        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded ml-2 flex-shrink-0">
          override
        </span>
      )}
    </>
  );

  return (
    <div className="card p-6">
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
        Contact Info
      </h3>

      <div className="space-y-4">
        {/* Email */}
        {email ? (
          emailComposeMode === 'MAILTO' ? (
            <a href={`mailto:${email}`} className={emailClass}>
              {emailInner}
            </a>
          ) : emailComposeMode === 'GMAIL' ? (
            <button type="button" onClick={onOpenInGmail} className={emailClass}>
              {emailInner}
            </button>
          ) : (
            <button type="button" onClick={onComposeEmail} className={emailClass}>
              {emailInner}
            </button>
          )
        ) : (
          <div className="flex items-center text-gray-400">
            <Mail className="h-5 w-5 mr-3 flex-shrink-0" />
            <span className="italic dark:text-gray-500">No email</span>
          </div>
        )}

        {/* Phone */}
        {phone ? (
          <a
            href={`tel:${phone}`}
            className="flex items-center text-gray-700 dark:text-gray-300 hover:text-blast-600 dark:hover:text-blast-400 transition-colors min-w-0"
          >
            <Phone className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
            <span className="truncate min-w-0">{formatPhone(phone)}</span>
            {person.crmPhone !== null && (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded ml-2 flex-shrink-0">
                override
              </span>
            )}
          </a>
        ) : (
          <div className="flex items-center text-gray-400">
            <Phone className="h-5 w-5 mr-3 flex-shrink-0" />
            <span className="italic dark:text-gray-500">No phone</span>
          </div>
        )}

        {/* Location (from primary org) */}
        {location && (
          <div className="flex items-center text-gray-700 dark:text-gray-300 min-w-0">
            <MapPin className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
            <span className="truncate min-w-0">{location}</span>
          </div>
        )}

        {/* Social Links */}
        {(person.twitterHandle || person.instagramHandle || person.linkedInUrl) && (
          <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-4">
              {person.twitterHandle && (
                <a
                  href={`https://twitter.com/${person.twitterHandle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                  title={`@${person.twitterHandle}`}
                >
                  <Twitter className="h-5 w-5" />
                </a>
              )}
              {person.instagramHandle && (
                <a
                  href={`https://instagram.com/${person.instagramHandle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-pink-500 transition-colors"
                  title={`@${person.instagramHandle}`}
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {person.linkedInUrl && (
                <a
                  href={person.linkedInUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                  title="LinkedIn Profile"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
